import { ActionIcon, Stack, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import classes from '@/components/Discover/Feed/MembersList.module.css'
import Loading from '@/components/Loading'
import VoteDetailsModal from '@/components/Modals/Governance/VoteDetailsModal'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import TooltipText from '@/components/TooltipText'
import { bronzeColor, ProcessedBalance, silverColor } from '@/utils/utils'

interface VotesCardProps {
  index: number
  nominee: string
  phase: 'nomination' | 'campaign' | 'voting' | 'results'
  isBreakpoint: boolean | undefined
  type: 'yours' | 'all'
  votes?: ProcessedBalance
  loading: boolean
  removed: boolean
  isError?: boolean
  refetchData?: () => void
}
const VotesCard: React.FC<VotesCardProps> = ({
  index,
  phase,
  nominee,
  isBreakpoint,
  type,
  votes,
  loading,
  isError,
  refetchData,
  removed,
}) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const [openedProof, { open: openProof, close: closeProof }] = useDisclosure()
  const results = useMemo(() => phase === 'results', [phase])

  const handleOnClick = useCallback(() => {
    if (type === 'yours' && results && votes && !isError) {
      return openProof()
    }
    return undefined
  }, [type, results])

  const text = useMemo(() => {
    if (type === 'yours') {
      return t('components.governance.votesCard.yourVotes', 'Your Votes')
    }
    return t('components.governance.votesCard.allVotes', 'All Votes')
  }, [type, t])

  return (
    <>
      <Stack
        gap={0}
        className={
          !(type === 'yours' && results && votes && !isError)
            ? classes.memberNoHover
            : classes.member
        }
        style={{
          gap: isBreakpoint ? '5px' : '0px',
          flexDirection: isBreakpoint ? 'row' : 'column',
          width: isBreakpoint ? '100%' : '150px',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor:
            results && index === 0
              ? theme.colors.barChartYellow[0]
              : results && index === 1
                ? silverColor
                : results && index === 2
                  ? bronzeColor
                  : removed
                    ? theme.colors.notification[0]
                    : '',
          backgroundColor: removed ? theme.colors.background2[0] : '',
        }}
        onClick={handleOnClick}
      >
        {loading ? (
          <Loading size="15px" imageLoaderHeight="15px" />
        ) : (
          <>
            {!isError ? (
              <Tooltip label={votes?.fullValue} offset={0}>
                <Title
                  order={5}
                  style={{
                    color: removed ? theme.colors.notification[0] : theme.colors.title[0],
                  }}
                >
                  {votes ? votes.display : '0'}
                </Title>
              </Tooltip>
            ) : (
              <ActionIcon onClick={refetchData}>
                <IconRefresh size={20} color={theme.colors.notification[0]} />
              </ActionIcon>
            )}
          </>
        )}
        <TooltipText text={text} />
      </Stack>
      <ModalSkeleton
        title={t('components.governance.modal.voteDetails.title', 'Vote Details')}
        onClose={closeProof}
        opened={openedProof}
      >
        <VoteDetailsModal nominee={nominee} close={closeProof} />
      </ModalSkeleton>
    </>
  )
}

export default VotesCard
